import { ILeanBrandTile as ILeanBrandTileAdapters } from "@faire/retailer-visitor-shared/components/LeanBrandTileRenderer/ILeanBrandTileAdapters";
import { LOAD_UPCOMING_PROMO_HEADER } from "@faire/retailer-visitor-shared/features/FaireMarkets/consts";
import { readBundledData } from "@faire/retailer-visitor-shared/lib/bundledData";
import { getRetailer } from "@faire/retailer-visitor-shared/serialized-data/getRetailer";
import { useSettingDecentralizePageData } from "@faire/retailer-visitor-shared/settings/useSettingDecentralizePageData";
import removeFromBoard from "@faire/web-api--source/endpoints/www/api/v2/retailers/retailerToken/boards/boardToken/brands/brandToken/delete";
import addToBoard from "@faire/web-api--source/endpoints/www/api/v2/retailers/retailerToken/boards/boardToken/items/post";
import { QueryParameters } from "@faire/web-api--source/endpoints/www/api/v2/retailers/retailerToken/boards/boardToken/lean-brand-tiles/get";
import {
  useQuery as useFetchItemsForBoard,
  getQueryKey as getFetchItemsForBoardKey,
} from "@faire/web-api--source/endpoints/www/api/v2/retailers/retailerToken/boards/boardToken/lean-brand-tiles/get-hooks";
import { useQuery as useFetchEventBoard } from "@faire/web-api--source/endpoints/www/api/v2/retailers/retailerToken/boards/event/get-hooks";
import { ICreateRetailerBoardItemRequest } from "@faire/web-api--source/indigofair/data/ICreateRetailerBoardItemRequest";
import { IGetRetailerBoardLeanBrandTilesPaginationResponse } from "@faire/web-api--source/indigofair/data/IGetRetailerBoardLeanBrandTilesPaginationResponse";
import { ILeanBrandTile } from "@faire/web-api--source/indigofair/data/ILeanBrandTile";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";

const FETCH_BOARD_ITEMS_PARAMETERS = QueryParameters.build({
  pageSize: 1000, // we are not expecting a huge amount of brands to need pagination,
  load_upcoming_market_promo: true,
});

export const useMyMarketList = () => {
  const queryClient = useQueryClient();
  const shouldDecentralizePageData = useSettingDecentralizePageData();

  const bundledEventBoard = useMemo(() => {
    return readBundledData("event_board");
  }, []);

  const retailerToken = getRetailer()?.token;

  const { data: retailerBoardEndpointData } = useFetchEventBoard(
    retailerToken ?? "",
    {
      refetchOnMount: false,
      staleTime: Infinity,
      enabled: shouldDecentralizePageData || !bundledEventBoard?.board,
    }
  );

  const retailerBoard = bundledEventBoard ?? retailerBoardEndpointData;
  const boardToken = retailerBoard?.board?.token;

  const queryKey = getFetchItemsForBoardKey(
    retailerToken ?? "",
    boardToken ?? "",
    FETCH_BOARD_ITEMS_PARAMETERS,
    { headers: new Headers(LOAD_UPCOMING_PROMO_HEADER) }
  );

  const addBrandToResponse = useCallback(
    (brandToken: string) => {
      queryClient.setQueryData<
        IGetRetailerBoardLeanBrandTilesPaginationResponse | undefined
      >(queryKey, (data) => {
        return IGetRetailerBoardLeanBrandTilesPaginationResponse.build({
          ...data,
          brand_tiles: [
            ...(data?.brand_tiles ?? []),
            ILeanBrandTile.build({
              brand: ILeanBrandTile.IBrand.build({
                token: brandToken,
              }),
            }),
          ],
        });
      });
    },
    [queryClient, queryKey]
  );

  const removeBrandFromResponse = useCallback(
    (brandToken: string) => {
      queryClient.setQueryData<
        IGetRetailerBoardLeanBrandTilesPaginationResponse | undefined
      >(queryKey, (data) => {
        return IGetRetailerBoardLeanBrandTilesPaginationResponse.build({
          ...data,
          brand_tiles: data?.brand_tiles?.filter((brandTile) => {
            return brandTile.brand?.token !== brandToken;
          }),
        });
      });
    },
    [queryClient, queryKey]
  );

  // have to use isPending instead of isLoading because endpoint does not fetch on mount (fetchEventBoard endpoint must return first before fetching items)
  const { data: retailerBoardItemsResponse, isPending: isBoardItemsLoading } =
    useFetchItemsForBoard(
      retailerToken ?? "",
      boardToken ?? "",
      FETCH_BOARD_ITEMS_PARAMETERS,
      {
        enabled: !!boardToken,
        refetchOnMount: false,
      },
      { headers: new Headers(LOAD_UPCOMING_PROMO_HEADER) }
    );

  const retailerBoardItems = retailerBoardItemsResponse?.brand_tiles;

  const items = useMemo(() => {
    if (!retailerBoardItems) {
      return undefined;
    }

    return (
      retailerBoardItems.map(ILeanBrandTileAdapters.fromBoardLeanBrandTile) ??
      []
    );
  }, [retailerBoardItems]);

  const boardItemsIsAdded = useMemo(() => {
    const boardItemsAddedMap = {} as Record<string, boolean>;

    retailerBoardItems?.forEach((brandTile) => {
      if (brandTile.brand?.token) {
        boardItemsAddedMap[brandTile.brand.token] = true;
      }
    });

    return boardItemsAddedMap;
  }, [retailerBoardItems]);

  const addBrandToList = useCallback(
    async (brandToken: string | undefined) => {
      if (!retailerToken || !boardToken || !brandToken) {
        return;
      }

      addBrandToResponse(brandToken);

      try {
        await addToBoard(
          retailerToken,
          boardToken,
          ICreateRetailerBoardItemRequest.build({
            brand_token: brandToken,
            select_best_image: true,
          })
        );

        queryClient.invalidateQueries({
          queryKey,
        });
      } catch (error) {
        removeBrandFromResponse(brandToken);
      }
    },
    [
      addBrandToResponse,
      boardToken,
      queryClient,
      queryKey,
      removeBrandFromResponse,
      retailerToken,
    ]
  );

  const deleteBrandFromList = useCallback(
    async (brandToken: string | undefined) => {
      if (!retailerToken || !boardToken || !brandToken) {
        return;
      }

      removeBrandFromResponse(brandToken);

      try {
        await removeFromBoard(retailerToken, boardToken, brandToken);
        queryClient.invalidateQueries({
          queryKey,
        });
      } catch (error) {
        addBrandToResponse(brandToken);
      }
    },
    [
      addBrandToResponse,
      boardToken,
      queryClient,
      queryKey,
      removeBrandFromResponse,
      retailerToken,
    ]
  );

  return {
    items,
    boardItemsIsAdded,
    addBrandToList,
    deleteBrandFromList,
    isLoading: isBoardItemsLoading,
  };
};
